import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'

export default function Tilbud() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    address: '',
    phone: '',
    email: '',
    cvr: '',
    customer_type: 'Privat', // Default
    description: '',
    website_url: '' // Honeypot
  })
  const [status, setStatus] = useState({ type: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/pages')
      .then(res => {
        if (!res.ok) throw new Error('Kunne ikke hente data')
        return res.json()
      })
      .then(data => {
        const tilbudPage = data.find(p => p.page_slug === 'tilbud')
        setPageData(tilbudPage)
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus({ type: '', message: '' })

    // Validation
    if (!formData.name || !formData.address || !formData.phone || !formData.email || !formData.cvr || !formData.description) {
      setStatus({ 
        type: 'error', 
        message: 'Oops! Der gik noget galt, måske du mangler et felt?' 
      })
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setStatus({ 
          type: 'success', 
          message: 'Tak for din henvendelse, vi kommer retur Asap!' 
        })
        setFormData({
          name: '',
          company_name: '',
          address: '',
          phone: '',
          email: '',
          cvr: '',
          customer_type: 'Privat',
          description: '',
          website_url: ''
        })
      } else {
        setStatus({ 
          type: 'error', 
          message: 'Oops! Der gik noget galt, måske du mangler et felt?' 
        })
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Der opstod en netværksfejl. Kontroller din internetforbindelse og prøv igen.' 
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange"></i>
          <p className="text-slate-500 font-medium">Indlæser tilbudsside...</p>
        </div>
      </div>
    )
  }

  // Fallback data matching the scrape exactly if CMS fetch fails
  const fallbackData = {
    title: "Indhent tilbud",
    meta_title: "Indhent tilbud - Trapezdk",
    meta_description: "Her kan du indhente et tilbud på jeres kommende projekt. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar idag.",
    content: `<h1>Indhent et tilbud</h1><p>Her kan du indhente et tilbud på jeres kommende projekt.</p><p>Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar idag.</p>`,
    images: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/677bb8497d4f7a21e927c0d8-sk-c3-a6rmbillede-202025-01-06-20120211.png"]
  }

  const data = pageData || fallbackData

  return (
    <div className="w-full">
      <Helmet>
        <title>{data.meta_title || "Indhent tilbud - Trapezdk"}</title>
        <meta name="description" content={data.meta_description || "Her kan du indhente et tilbud på jeres kommende projekt. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt."} />
        <meta property="og:title" content={data.meta_title || "Indhent tilbud - Trapezdk"} />
        <meta property="og:description" content={data.meta_description || "Her kan du indhente et tilbud på jeres kommende projekt. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt."} />
        <meta property="og:image" content={data.images?.[0] || ""} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Page Header */}
      <section className="relative text-white py-24 lg:py-32 overflow-hidden bg-slate-950">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6784d4ee8815f9c670344925-tts-20sag.jpg" 
            alt="Trapezplader baggrund" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/85 to-slate-950/80"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-left">
          <span className="text-brand-orange font-bold text-sm tracking-wider uppercase">Få et overslag</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-2">Indhent tilbud</h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Form Info & Image */}
            <div className="lg:col-span-5 space-y-8 text-left">
              <div>
                <h2 className="text-3xl font-extrabold text-brand-blue mb-4">Indhent et tilbud</h2>
                <div className="h-1 w-16 bg-brand-orange rounded-full mb-6"></div>
                <div className="space-y-4 text-slate-600 leading-relaxed text-base">
                  <p>Her kan du indhente et tilbud på jeres kommende projekt.</p>
                  <p>Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar idag.</p>
                </div>
              </div>

              {/* Call CTA Card */}
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 flex gap-4 items-center">
                <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center text-brand-orange shrink-0">
                  <i className="fa-solid fa-phone text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">Brug for hurtigt svar?</h4>
                  <a href="tel:+4522141149" className="text-brand-orange hover:underline font-semibold text-base block mt-0.5 whitespace-nowrap">
                    Ring til os på +45 22 14 11 49
                  </a>
                </div>
              </div>

              {/* Scraped Image */}
              <div className="rounded-none overflow-hidden border border-slate-200 shadow-sm relative">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/677bb8497d4f7a21e927c0d8-sk-c3-a6rmbillede-202025-01-06-20120211.png" 
                  alt="Trapezplader profil" 
                  className="w-full h-auto object-cover rounded-none"
                />
              </div>
            </div>

            {/* Right Column: Comprehensive Tilbudsformular */}
            <div className="lg:col-span-7 bg-slate-50 p-6 sm:p-10 rounded-2xl border border-slate-100 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-brand-blue mb-6 text-left">Udfyld tilbudsformular</h3>

              {status.message && (
                <div className={`p-4 rounded-xl mb-6 text-sm font-medium flex items-start gap-3 text-left ${
                  status.type === 'success' 
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                    : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}>
                  <i className={`fa-solid ${status.type === 'success' ? 'fa-circle-check text-emerald-500' : 'fa-circle-exclamation text-rose-500'} mt-0.5 text-base`}></i>
                  <div>{status.message}</div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5 text-left">
                {/* Honeypot Field - Absolutely hidden from humans */}
                <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Navn */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Navn <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  {/* Firma navn */}
                  <div>
                    <label htmlFor="company_name" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Firma navn
                    </label>
                    <input
                      id="company_name"
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
                      placeholder="Evt. firmanavn"
                    />
                  </div>
                </div>

                {/* Adresse, Post nr. By */}
                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Adresse, Post nr. By <span className="text-brand-orange">*</span>
                  </label>
                  <input
                    id="address"
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
                    placeholder="F.eks. Rosbjergvej 54C, 8220 Brabrand"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Telefonnummer */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Telefonnummer <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
                      placeholder="Dit telefonnummer"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Email <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
                      placeholder="din@email.dk"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Cvr. Nummer */}
                  <div>
                    <label htmlFor="cvr" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Cvr. Nummer <span className="text-brand-orange">*</span>
                    </label>
                    <input
                      id="cvr"
                      type="text"
                      name="cvr"
                      required
                      value={formData.cvr}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
                      placeholder="Skriv CVR eller 'Intet' for privat"
                    />
                  </div>

                  {/* Privat eller Erhverv */}
                  <div>
                    <label htmlFor="customer_type" className="block text-sm font-semibold text-slate-700 mb-1.5">
                      Privat eller Erhverv <span className="text-brand-orange">*</span>
                    </label>
                    <select
                      id="customer_type"
                      name="customer_type"
                      required
                      value={formData.customer_type}
                      onChange={handleChange}
                      className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm"
                    >
                      <option value="Privat">Privat</option>
                      <option value="Erhverv">Erhverv</option>
                    </select>
                  </div>
                </div>

                {/* Beskrivelse */}
                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Beskrivelse af projektet <span className="text-brand-orange">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    rows="5"
                    value={formData.description}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange transition-all text-sm resize-none"
                    placeholder="Beskriv jeres projekt, spændvidde, arealkrav eller andre specifikationer her..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 bg-brand-orange hover:bg-brand-orange-hover disabled:bg-slate-400 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/20 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin"></i>
                      <span>Indsender tilbud...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane"></i>
                      <span>Send tilbudsanmodning</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
